import React, { useState, useRef, useEffect } from 'react';
import { sendChatMessage, getConfidenceDisplay } from '../../services/chatApi';
import './Chat.css';

/**
 * ChatModule - Multi-AI Property Chat Component
 *
 * Provides an intelligent chat interface that queries multiple AI models
 * (GPT-4, Claude 3.5, Gemini 2.0) about property data and returns
 * consensus responses with transparency into individual model answers.
 *
 * @param {string} internalId - Order internal ID (required)
 * @param {string} userId - User ID for tracking (optional)
 * @param {boolean} showModelResponses - Show individual model responses (default: true)
 * @param {string} initialQuestion - Pre-populate the input with a question (optional)
 */
function ChatModule({ internalId, userId = 'user', showModelResponses = true, initialQuestion = '' }) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState(initialQuestion);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);
  const [expandedModels, setExpandedModels] = useState({});
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSendMessage = async () => {
    const question = inputValue.trim();

    if (!question) {
      return;
    }

    if (!internalId) {
      setError('No property selected. Please select a property first.');
      return;
    }

    // Add user message to chat
    const userMessage = {
      type: 'user',
      content: question,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setError(null);

    try {
      // Call chat API
      const response = await sendChatMessage(internalId, question, sessionId, userId);

      // Store session ID for conversation continuity
      if (response.sessionId && !sessionId) {
        setSessionId(response.sessionId);
      }

      // Add AI response to chat
      const aiMessage = {
        type: 'ai',
        content: response.consensusResponse,
        confidence: response.confidence,
        agreementScore: response.agreementScore,
        consensusSource: response.consensusSource,
        modelResponses: response.modelResponses,
        metadata: response.metadata,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (err) {
      console.error('[CHAT] Error:', err);
      setError(err.message || 'Failed to get response. Please try again.');

      // Add error message to chat
      const errorMessage = {
        type: 'error',
        content: err.message || 'Failed to get response. Please try again.',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleModelExpansion = (messageIndex, modelName) => {
    const key = `${messageIndex}-${modelName}`;
    setExpandedModels(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const renderMessage = (message, index) => {
    if (message.type === 'user') {
      return (
        <div key={index} className="chat-message chat-message-user">
          <div className="chat-message-avatar">You</div>
          <div className="chat-message-content">
            <p>{message.content}</p>
            <span className="chat-message-timestamp">
              {new Date(message.timestamp).toLocaleTimeString()}
            </span>
          </div>
        </div>
      );
    }

    if (message.type === 'error') {
      return (
        <div key={index} className="chat-message chat-message-error">
          <div className="chat-message-avatar">⚠️</div>
          <div className="chat-message-content">
            <p className="chat-error-text">{message.content}</p>
            <span className="chat-message-timestamp">
              {new Date(message.timestamp).toLocaleTimeString()}
            </span>
          </div>
        </div>
      );
    }

    if (message.type === 'ai') {
      const confidenceDisplay = getConfidenceDisplay(message.confidence);

      return (
        <div key={index} className="chat-message chat-message-ai">
          <div className="chat-message-avatar chat-avatar-sunny">Sunny</div>
          <div className="chat-message-content">
            <div className="chat-consensus-header">
              <span className="chat-consensus-badge" style={{ backgroundColor: confidenceDisplay.color }}>
                {confidenceDisplay.text} ({(message.confidence * 100).toFixed(0)}%)
              </span>
              <span className="chat-consensus-source">via {message.consensusSource}</span>
            </div>

            <p className="chat-consensus-response">{message.content}</p>

            {showModelResponses && message.modelResponses && (
              <div className="chat-model-responses">
                <div className="chat-model-header">Individual Model Responses:</div>

                {Object.entries(message.modelResponses).map(([modelName, modelData]) => {
                  const key = `${index}-${modelName}`;
                  const isExpanded = expandedModels[key];

                  return (
                    <div key={modelName} className="chat-model-item">
                      <button
                        className="chat-model-toggle"
                        onClick={() => toggleModelExpansion(index, modelName)}
                      >
                        <span className="chat-model-name">{modelData.model}</span>
                        <span className="chat-model-meta">
                          {modelData.tokens} tokens · {modelData.latency?.toFixed(2)}s
                        </span>
                        <span className="chat-toggle-icon">{isExpanded ? '▼' : '▶'}</span>
                      </button>

                      {isExpanded && (
                        <div className="chat-model-response-text">
                          {modelData.response}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {message.metadata && (
              <div className="chat-metadata">
                <span>Total: {message.metadata.totalTokens} tokens</span>
                <span>${message.metadata.totalCost?.toFixed(4)}</span>
                <span>{message.metadata.totalLatency?.toFixed(2)}s</span>
              </div>
            )}

            <span className="chat-message-timestamp">
              {new Date(message.timestamp).toLocaleTimeString()}
            </span>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="chat-module">
      <div className="chat-header">
        <div className="chat-header-title">
          <span className="chat-title-icon">💬</span>
          <h3>Property Chat Assistant</h3>
        </div>
        <div className="chat-header-subtitle">
          Ask questions about property data, title issues, or transaction details
        </div>
      </div>

      <div className="chat-messages-container">
        {messages.length === 0 && (
          <div className="chat-welcome">
            <div className="chat-welcome-icon">👋</div>
            <h4>Welcome to Sunny AI Property Chat</h4>
            <p>I'm your intelligent title insurance assistant powered by multiple AI models.</p>
            <div className="chat-welcome-suggestions">
              <p className="chat-suggestions-title">Try asking:</p>
              <button
                className="chat-suggestion-button"
                onClick={() => setInputValue("Are there any title issues I should be aware of?")}
              >
                "Are there any title issues I should be aware of?"
              </button>
              <button
                className="chat-suggestion-button"
                onClick={() => setInputValue("What is the current owner's name?")}
              >
                "What is the current owner's name?"
              </button>
              <button
                className="chat-suggestion-button"
                onClick={() => setInputValue("Is this property ready for title commitment?")}
              >
                "Is this property ready for title commitment?"
              </button>
              <button
                className="chat-suggestion-button"
                onClick={() => setInputValue("Summarize the loan details")}
              >
                "Summarize the loan details"
              </button>
            </div>
          </div>
        )}

        {messages.map((message, index) => renderMessage(message, index))}

        {isLoading && (
          <div className="chat-message chat-message-ai chat-message-loading">
            <div className="chat-message-avatar chat-avatar-sunny">Sunny</div>
            <div className="chat-message-content">
              <div className="chat-loading-indicator">
                <div className="chat-loading-dot"></div>
                <div className="chat-loading-dot"></div>
                <div className="chat-loading-dot"></div>
              </div>
              <p className="chat-loading-text">
                Consulting GPT-4, Claude 3.5, and Gemini 2.0...
              </p>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-container">
        {error && (
          <div className="chat-error-banner">
            ⚠️ {error}
          </div>
        )}

        <div className="chat-input-wrapper">
          <textarea
            ref={inputRef}
            className="chat-input"
            placeholder="Ask a question about this property..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            rows={2}
          />
          <button
            className="chat-send-button"
            onClick={handleSendMessage}
            disabled={isLoading || !inputValue.trim()}
          >
            {isLoading ? (
              <span className="chat-button-loading">⏳</span>
            ) : (
              <span className="chat-button-send">Send</span>
            )}
          </button>
        </div>

        <div className="chat-input-footer">
          <span className="chat-model-info">
            Powered by GPT-4, Claude 3.5 Sonnet, and Gemini 2.0 Flash
          </span>
        </div>
      </div>
    </div>
  );
}

export default ChatModule;
